import { FilterParams } from '@/types'
import type {
	DeliveryClient,
	IContentItem,
	Responses,
	SortOrder,
} from '@kontent-ai/delivery-sdk'
import { client } from './client'
import { fnReturnData } from './fnReturnData'

export function getAllItemsByType<T extends IContentItem>({
	type,
	depth = 1,
	order = null,
	elementsParameter = null,
	containsFilter = null,
	allFilter = null,
	anyFilter = null,
	equalsFilter = null,
	notEmptyFilter = null,
	notEqualsFilter = null,
	inFilter = null,
	limit = null,
	skip = null,
	preview,
	kontentClient = client,
}: {
	type: string
	depth?: number
	order?: { element: string; sortOrder: SortOrder }
	elementsParameter?: string[]
	containsFilter?: FilterParams
	allFilter?: FilterParams
	anyFilter?: FilterParams
	equalsFilter?: { element: string; value: string }
	notEmptyFilter?: { element: string }
	notEqualsFilter?: { element: string; value: string }
	inFilter?: FilterParams
	limit?: number
	skip?: number
	preview: boolean
	kontentClient?: DeliveryClient
}): Promise<Responses.IListContentItemsResponse<T>> {
	let temp = kontentClient.items<T>().type(type).depthParameter(depth)
	if (order) {
		temp = temp.orderParameter(order.element, order.sortOrder)
	}
	if (elementsParameter) {
		temp = temp.elementsParameter(elementsParameter)
	}
	if (containsFilter) {
		temp = temp.containsFilter(containsFilter.element, containsFilter.value)
	}
	if (allFilter) {
		temp = temp.allFilter(allFilter.element, allFilter.value)
	}
	if (anyFilter) {
		temp = temp.anyFilter(anyFilter.element, anyFilter.value)
	}
	if (equalsFilter) {
		temp = temp.equalsFilter(equalsFilter.element, equalsFilter.value)
	}
	if (notEmptyFilter) {
		temp = temp.notEmptyFilter(notEmptyFilter.element)
	}
	if (notEqualsFilter) {
		temp = temp.notEqualsFilter(
			notEqualsFilter.element,
			notEqualsFilter.value,
		)
	}
	if (inFilter) {
		temp = temp.inFilter(inFilter.element, inFilter.value)
	}
	if (limit) {
		temp = temp.limitParameter(limit)
	}
	if (skip) {
		temp = temp.skipParameter(skip)
	}
	return temp
		.queryConfig({ usePreviewMode: preview })
		.toPromise()
		.then(fnReturnData)
}

export async function getAllItemsByTypeWithPagination<T extends IContentItem>(
	params: Omit<Parameters<typeof getAllItemsByType>[0], 'limit' | 'skip'> & {
		pageSize?: number
	},
): Promise<Responses.IListContentItemsResponse<T>> {
	const { pageSize = 1000, ...restParams } = params
	let allItems: T[] = []
	let allLinkedItems: { [key: string]: IContentItem } = {}
	let skip = 0
	let hasMoreItems = true
	let pagination = undefined

	while (hasMoreItems) {
		const response = await getAllItemsByType<T>({
			...restParams,
			limit: pageSize,
			skip,
		})

		allItems = allItems.concat(response.items)
		// Merge linkedItems from each response
		Object.assign(allLinkedItems, response.linkedItems)
		pagination = response.pagination

		// Check if we have more items to fetch
		// Handle both cases: totalCount is available or not
		if (
			pagination.totalCount !== null &&
			pagination.totalCount !== undefined
		) {
			hasMoreItems = allItems.length < pagination.totalCount
		} else {
			// If totalCount is null, check if we got fewer items than requested
			hasMoreItems = response.items.length === pageSize
		}

		skip += pageSize
	}

	// Return a response that looks like the original but with all items
	return {
		items: allItems,
		pagination: {
			...pagination,
			count: allItems.length,
			limit: allItems.length,
			offset: 0,
		},
		linkedItems: allLinkedItems,
	} as Responses.IListContentItemsResponse<T>
}
